import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import type { MainMenuItem } from '@layout/MainMenu/MainMenu';
import { OffCanvasPane } from '@layout/OffCanvasPane/OffCanvasPane';
import { MobileSearchBox } from '@layout/MobileSearchBox/MobileSearchBox';
import { useTranslation } from 'next-i18next';

export interface MobileMainMenuButtonProps {
  onClick: () => void;
}

export const MobileMainMenuButton = ({ onClick }: MobileMainMenuButtonProps) => {
  const { t } = useTranslation(['product/mobileMainMenu']);

  return (
    <div className="font-medium cursor-pointer" onClick={() => onClick()} data-testid="mobilemainmenu-menu-button">
      <Icon id={IconID.SEARCH} primary={false} size={10} />
      &nbsp;&nbsp;
      <Icon id={IconID.MENU} primary={false} size={10} />
      &nbsp;&nbsp;
      {t('NAVIGATION.MOBILE_MENU')}
    </div>
  );
};

export interface MobileMenuPaneProps {
  title: string;
  visible: boolean;
  items: MainMenuItem[];
  depth: number;
  onClose: () => void;
}

export const MobileMenuPane = ({ title, visible, items, depth, onClose }: MobileMenuPaneProps) => {
  const [activeMenu, setActiveMenu] = useState(-1);

  useEffect(() => {
    if (!visible) {
      setActiveMenu(-1);
    }
  }, [visible]);

  return (
    <>
      <div
        className={classNames(
          'cursor-pointer flex flex-col bg-white absolute top-0 w-full h-full',
          'transform-gpu transition-transform divide-y divide-gray-100',
          visible ? undefined : 'translate-x-full'
        )}
        style={{ zIndex: depth + 10 }}
      >
        <div onClick={onClose} className="font-bold py-7 px-8 flex-col">
          <Icon id={IconID.ARROW_LEFT} primary size={5} />
          <span className="ml-5">{title}</span>
        </div>

        <ul className="relative divide-y divide-gray-100 overflow-y-auto flex-grow">
          {items.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`mmm-item-${depth}-${index}`}>
              {item.children ? (
                <div className="flex py-7 px-8" onClick={() => setActiveMenu(index)}>
                  <div className="flex-grow">{item.title}</div>
                  <div>
                    <Icon id={IconID.ARROW_RIGHT} primary size={5} />
                  </div>
                </div>
              ) : (
                <a className="flex py-7 px-8" href={item.url}>
                  {item.title}
                </a>
              )}
            </li>
          ))}
        </ul>
      </div>
      {items.map(
        (item, index) =>
          item.children && (
            <MobileMenuPane
              /* eslint-disable-next-line react/no-array-index-key */
              key={`mmm-pane-${depth}-${index}`}
              onClose={() => setActiveMenu(-1)}
              title={item.title}
              visible={activeMenu === index}
              depth={depth + 1}
              items={item.children}
            />
          )
      )}
    </>
  );
};

export interface MobileMainMenuProps {
  mainMenuItems: MainMenuItem[];
  visible: boolean;
  onClose: () => void;
}

export const MobileMainMenu = ({ mainMenuItems, visible, onClose }: MobileMainMenuProps) => {
  const [activeMenu, setActiveMenu] = useState(-1);

  useEffect(() => {
    if (!visible) {
      setActiveMenu(-1);
    }
  }, [visible]);

  return (
    <>
      <OffCanvasPane
        className="
          cursor-default
          w-full
          md:w-1/2
          h-screen
          border-l
          border-gray-100
          divide-y
          divide-gray-100
          flex
          flex-col
          z-50
        "
        visible={visible}
      >
        <div className="relative flex flex-row" data-testid="mobilemainmenu">
          <MobileSearchBox />
          <div
            data-testid="mobilemainmenu-close"
            className="cursor-pointer relative top-0 right-0 bg-igus-primary text-white py-7 px-9 z-10"
            onClick={() => {
              setActiveMenu(-1);
              onClose();
            }}
          >
            <Icon id={IconID.CLOSE_CROSS} primary={false} size={7} color={IconColor.WHITE} />
          </div>
        </div>
        <nav className={classNames('relative bg-white text-black text-base flex-grow')}>
          <ul className="cursor-pointer relative divide-y divide-gray-100">
            {mainMenuItems.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`mmm-item-0-${index}`} className="flex py-7 px-8" onClick={() => setActiveMenu(index)}>
                <div className="flex-grow">{item.title}</div>
                <div>
                  <Icon id={IconID.ARROW_RIGHT} primary size={5} />
                </div>
              </li>
            ))}
            <li />
          </ul>
          {mainMenuItems.map(
            (item, index) =>
              item.children && (
                <MobileMenuPane
                  /* eslint-disable-next-line react/no-array-index-key */
                  key={`mmm-pane-0-${index}`}
                  onClose={() => setActiveMenu(-1)}
                  title={item.title}
                  visible={activeMenu === index}
                  items={item.children}
                  depth={1}
                />
              )
          )}
        </nav>
      </OffCanvasPane>
      {visible && <div className="fixed top-0 left-0 bottom-0 right-0 opacity-60 bg-gray-100 z-40" />}
    </>
  );
};
